import { ResourceArray, WithId } from "@medplum/core";
import { BundleEntry, CodeableConcept, List, Observation } from "@medplum/fhirtypes";

import { addDays, formatISO, parseISO, startOfDay } from "date-fns";

import { USUAL_GESTATION_DAYS } from "@/pregnancy/constants/gestational-age";
import { middleOfDay, usDateFormat } from "@/utils/datetime";
import { generateFullUrl, joinReferenceString } from "@/utils/medplum";

import {
  EDD_CODING,
  EDD_CODING_CUSTOM,
  EDD_CODING_LMP,
  EDD_CODING_ULTRASOUND,
  EDD_DISPLAY_CODING,
  GA_COMPOSITE_FROM_ULTRASOUND_CODING,
  ULTRASOUND_DATE_CODING,
} from "../constants/edd";

import {
  buildLMPBundleEntry,
  buildLMPReliabilityBundleEntry,
  buildUpdatedLMPBundleEntry,
} from "./lmp";
import { selectObservationByStatusPrecedence } from "./observation-precedence";

// --- Predicates --------------------------------------------------------------

/**
 * Returns whether an Observation is the pregnancy EDD wrapper with a usable date.
 *
 * @param observation - FHIR Observation to test
 * @returns `true` when coding matches the EDD wrapper, a date is present, and status is not entered-in-error
 */
export function isEddWrapperWithValue(observation: Observation): boolean {
  const isEddWrapper = observation.code?.coding?.some(
    (coding) => coding.system === EDD_CODING.system && coding.code === EDD_CODING.code,
  );
  return Boolean(
    isEddWrapper && observation.valueDateTime && observation.status !== "entered-in-error",
  );
}

// --- Selection ---------------------------------------------------------------

function observationsOnPregnancyList(
  list: List,
  observationByReference: Map<string, Observation>,
): Observation[] {
  return (list.entry ?? []).reduce<Observation[]>((matched, entry) => {
    const referenceString = entry.item?.reference;
    const observation = referenceString ? observationByReference.get(referenceString) : undefined;
    if (observation?.valueDateTime && observation.status !== "entered-in-error") {
      matched.push(observation);
    }
    return matched;
  }, []);
}

/**
 * Resolves the EDD linked to a pregnancy `List`, from Observations already fetched in bulk. Use
 * when building records for many lists at once would cost a per-list search.
 *
 * Mirrors `buildPregnancyRecord`: prefers the stored EDD wrapper, then falls back to an EDD
 * derived from the list's LMP so both resolve to the same date for a given list.
 */
export function selectEddForPregnancyList(
  list: List,
  eddObservationByReference: Map<string, Observation>,
  lmpObservationByReference: Map<string, Observation>,
): string | undefined {
  const eddValue = selectObservationByStatusPrecedence(
    observationsOnPregnancyList(list, eddObservationByReference),
    { allowAnyStatus: true },
  )?.valueDateTime;

  if (eddValue) return eddValue;

  const lmpValue = selectObservationByStatusPrecedence(
    observationsOnPregnancyList(list, lmpObservationByReference),
  )?.valueDateTime;

  return lmpValue ? calculateEDDFromLastMenstrualPeriod(lmpValue) : undefined;
}

// --- Display -----------------------------------------------------------------

/**
 * Returns a US-formatted display string for an EDD date, or `"--"` if absent.
 *
 * @param edd - ISO date or datetime string for the estimated delivery date
 * @returns Formatted date string (e.g. `"04/14/2025"`) or `"--"`
 */
export function getEDDDisplay(edd: string | undefined): string {
  return edd ? usDateFormat(edd) : "--";
}

// --- Calculate-- -------------------------------------------------------------

/**
 * Calculates the estimated delivery date from a last menstrual period date.
 * Adds the standard gestation period (280 days) to the LMP and normalizes to noon.
 *
 * @param lastMenstrualPeriod - ISO date string for the last menstrual period
 * @returns ISO datetime string for the estimated delivery date (at noon UTC)
 */
export function calculateEDDFromLastMenstrualPeriod(lastMenstrualPeriod: string): string {
  return formatISO(
    middleOfDay(addDays(startOfDay(parseISO(lastMenstrualPeriod)), USUAL_GESTATION_DAYS))!,
  );
}

/**
 * Calculates the estimated delivery date from an ultrasound exam date and the
 * gestational age measured at that exam.
 *
 * Derives remaining pregnancy days by subtracting the measured gestational age
 * (in total days) from the standard gestation period (280 days), then adds that
 * remainder to the ultrasound date, normalized to noon.
 *
 * @param ultrasoundDate - ISO date string for the date of the ultrasound exam
 * @param weeks - Full weeks of gestational age measured at the ultrasound
 * @param days - Additional days of gestational age beyond the full weeks
 * @returns ISO datetime string for the estimated delivery date (at noon UTC)
 */
export function calculateEDDFromUltrasoundDate(
  ultrasoundDate: string,
  weeks: string | number | undefined,
  days: string | number | undefined,
): string {
  const gestationalAgeAtUltrasound = (Number(weeks) || 0) * 7 + (Number(days) || 0);
  const expectedRemainingPregnancyDays = USUAL_GESTATION_DAYS - gestationalAgeAtUltrasound;
  return formatISO(
    middleOfDay(addDays(startOfDay(parseISO(ultrasoundDate)), expectedRemainingPregnancyDays))!,
  );
}

// --- Build -------------------------------------------------------------------

/**
 * Builds a `BundleEntry` containing the primary EDD wrapper `Observation`
 * (LOINC 57060-6) for use in a FHIR transaction bundle.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueDateTime - ISO datetime string for the estimated delivery date
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 *   (e.g. `hasMember`, `focus`); core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the EDD wrapper `Observation`
 */
export function buildEDDBundleEntry(
  patientReferenceString: string,
  valueDateTime: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      code: { coding: [EDD_CODING] },
      status: "final",
      subject: { reference: patientReferenceString },
      valueDateTime,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds a `BundleEntry` for the EDD-to-display `Observation` (QH `forms/edd/display`),
 * which records which EDD method is currently shown to the user.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueCodeableConcept - CodeableConcept indicating the active EDD source
 *   (e.g. LMP, ultrasound)
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 * @returns `BundleEntry<Observation>` containing the EDD-to-display `Observation`
 */
export function buildEDDToDisplayBundleEntry(
  patientReferenceString: string,
  valueCodeableConcept: CodeableConcept,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [EDD_DISPLAY_CODING] },
      subject: { reference: patientReferenceString },
      valueCodeableConcept,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds a `BundleEntry` for an EDD-from-LMP `Observation` (LOINC 11779-6)
 * for use in a FHIR transaction bundle.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueDateTime - ISO datetime string for the EDD calculated from LMP
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 *   (e.g. `focus`); core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the EDD-from-LMP `Observation`
 */
export function buildEDDFromLastMenstrualPeriodBundleEntry(
  patientReferenceString: string,
  valueDateTime: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [EDD_CODING_LMP] },
      subject: { reference: patientReferenceString },
      valueDateTime,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds all `BundleEntry` items needed to record an LMP-derived EDD in a FHIR
 * transaction bundle: an LMP `Observation`, an EDD-from-LMP `Observation`, and
 * optionally an LMP reliability `Observation`.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param lastMenstrualPeriod - ISO date string for the last menstrual period; if omitted,
 *   no LMP or EDD entries are added
 * @param lastMenstrualPeriodReliability - Coded reliability value for the LMP; if omitted,
 *   no reliability entry is added
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 * @returns A tuple of `[edd, entries]` where `edd` is the calculated EDD ISO date string
 *   (or `undefined` if no LMP was provided) and `entries` is the array of bundle entries
 */
export function buildEDDObservationsFromLastMenstrualPeriod(
  patientReferenceString: string,
  lastMenstrualPeriod: string | undefined,
  lastMenstrualPeriodReliability: string | undefined,
  {
    request,
    resource,
    lmpObservations,
  }: {
    request?: BundleEntry["request"];
    resource?: Partial<Observation>;
    lmpObservations?: ResourceArray<WithId<Observation>>;
  } = {},
): [string | undefined, BundleEntry<Observation>[]] {
  const entry: BundleEntry<Observation>[] = [];
  let edd: string | undefined;

  if (!lastMenstrualPeriod && lmpObservations?.length) {
    const lmpBundleEntries = buildUpdatedLMPBundleEntry(lmpObservations);
    entry.push(...lmpBundleEntries);
  }

  if (lastMenstrualPeriod) {
    const lastMenstrualPeriodDatetime = formatISO(middleOfDay(lastMenstrualPeriod)!);
    edd = calculateEDDFromLastMenstrualPeriod(lastMenstrualPeriod);

    const lmpBundleEntry = buildLMPBundleEntry(
      patientReferenceString,
      lastMenstrualPeriodDatetime,
      {
        request,
        resource,
      },
    );

    const eddLmpBundleEntry = buildEDDFromLastMenstrualPeriodBundleEntry(
      patientReferenceString,
      edd,
      { request, resource },
    );

    entry.push(lmpBundleEntry, eddLmpBundleEntry);
  }

  if (lastMenstrualPeriodReliability) {
    const lmpReliabilityBundleEntry = buildLMPReliabilityBundleEntry(
      patientReferenceString,
      lastMenstrualPeriodReliability,
      { request, resource },
    );

    entry.push(lmpReliabilityBundleEntry);
  }

  return [edd, entry];
}

/**
 * Builds `PUT` bundle entries that cancel custom EDD `Observation`s when the client removes the
 * custom EDD.
 */
export function buildUpdatedCustomEDDBundleEntry(
  customEDDObservations: ResourceArray<WithId<Observation>>,
): BundleEntry<Observation>[] {
  return customEDDObservations.map((observation) => ({
    resource: {
      ...observation,
      status: "cancelled",
      code: { coding: [EDD_CODING_CUSTOM] },
    },
    request: {
      method: "PUT",
      url: joinReferenceString("Observation", observation.id),
    },
  }));
}

/**
 * Builds a `BundleEntry` for a custom EDD `Observation` (LOINC 11778-8)
 * for use in a FHIR transaction bundle.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueDateTime - ISO datetime string for the manually entered EDD
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields;
 *   core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the custom EDD `Observation`
 */
export function buildCustomEDDBundleEntry(
  patientReferenceString: string,
  valueDateTime: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [EDD_CODING_CUSTOM] },
      subject: { reference: patientReferenceString },
      valueDateTime,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds a `BundleEntry` for an ultrasound date `Observation` (LOINC 34970-4)
 * for use in a FHIR transaction bundle. Records the date of the ultrasound exam
 * from which the EDD-from-Ultrasound was derived.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueDateTime - ISO datetime string for the date of the ultrasound exam
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields;
 *   core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the ultrasound date `Observation`
 */

/* Builds a `BundleEntry` for an updated ultrasound date observation when the ultrasound date is removed by the client */
export function buildUpdatedUltrasoundBundleEntry(
  ultrasoundObservations: ResourceArray<WithId<Observation>>,
): BundleEntry<Observation>[] {
  return ultrasoundObservations.map((observation) => ({
    request: {
      method: "PUT",
      url: joinReferenceString("Observation", observation.id),
    },
    resource: {
      ...observation,
      status: "cancelled",
      code: { coding: [ULTRASOUND_DATE_CODING] },
    },
  }));
}
export function buildUltrasoundDateBundleEntry(
  patientReferenceString: string,
  valueDateTime: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [ULTRASOUND_DATE_CODING] },
      subject: { reference: patientReferenceString },
      valueDateTime,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds a `BundleEntry` for a GA composite from ultrasound `Observation`
 * (LOINC 11888-5) for use in a FHIR transaction bundle. Stores gestational age
 * in total days (`weeks * 7 + days`) as `valueInteger`.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param gestationalAgeWeeks - Full weeks of gestational age at the time of the ultrasound
 * @param gestationalAgeDays - Additional days of gestational age beyond the full weeks
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields;
 *   core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the GA composite `Observation`
 */
export function buildGACompositeFromUltrasoundBundleEntry(
  patientReferenceString: string,
  gestationalAgeWeeks: number,
  gestationalAgeDays: number,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [GA_COMPOSITE_FROM_ULTRASOUND_CODING] },
      subject: { reference: patientReferenceString },
      valueInteger: gestationalAgeWeeks * 7 + gestationalAgeDays,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds a `BundleEntry` for an EDD-from-Ultrasound `Observation` (LOINC 11781-2)
 * for use in a FHIR transaction bundle.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueDateTime - ISO datetime string for the EDD calculated from ultrasound
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 *   (e.g. `hasMember` for the ultrasound date, `focus`); core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the EDD-from-Ultrasound `Observation`
 */
export function buildEDDFromUltrasoundBundleEntry(
  patientReferenceString: string,
  valueDateTime: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [EDD_CODING_ULTRASOUND] },
      subject: { reference: patientReferenceString },
      valueDateTime,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/**
 * Builds all `BundleEntry` items needed to record an ultrasound-derived EDD in a FHIR
 * transaction bundle: an ultrasound date `Observation`, an EDD-from-Ultrasound `Observation`,
 * and optionally a gestational age composite `Observation`.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param ultrasoundDate - ISO date string for the ultrasound exam; if omitted, no ultrasound
 *   date or EDD entries are added
 * @param ultrasoundGestationalAgeWeeks - Gestational age in whole weeks at the time of the
 *   ultrasound; used to calculate the EDD and, if provided with days, to build a GA composite entry
 * @param ultrasoundGestationalAgeDays - Additional gestational age days beyond the full weeks;
 *   used alongside weeks to build a GA composite entry
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 * @returns A tuple of `[edd, entries]` where `edd` is the calculated EDD ISO date string
 *   (or `undefined` if no ultrasound date was provided) and `entries` is the array of bundle entries
 */
export function buildEDDObservationsFromUltrasound(
  patientReferenceString: string,
  ultrasoundDate: string | undefined,
  ultrasoundGestationalAgeWeeks: number | undefined,
  ultrasoundGestationalAgeDays: number | undefined,
  {
    request,
    resource,
    ultrasoundObservations,
  }: {
    request?: BundleEntry["request"];
    resource?: Partial<Observation>;
    ultrasoundObservations?: ResourceArray<WithId<Observation>>;
  } = {},
): [string | undefined, BundleEntry<Observation>[]] {
  const entry: BundleEntry<Observation>[] = [];
  let edd: string | undefined;

  if (!ultrasoundDate && ultrasoundObservations?.length) {
    const ultrasoundBundleEntries = buildUpdatedUltrasoundBundleEntry(ultrasoundObservations);
    entry.push(...ultrasoundBundleEntries);
  }

  if (ultrasoundDate) {
    const ultrasoundDatetime = formatISO(middleOfDay(ultrasoundDate)!);

    edd = calculateEDDFromUltrasoundDate(
      ultrasoundDate,
      ultrasoundGestationalAgeWeeks,
      ultrasoundGestationalAgeDays,
    );

    const ultrasoundDateBundleEntry = buildUltrasoundDateBundleEntry(
      patientReferenceString,
      ultrasoundDatetime,
    );

    const ultrasoundEDDBundleEntry = buildEDDFromUltrasoundBundleEntry(
      patientReferenceString,
      edd,
      { request, resource },
    );

    entry.push(ultrasoundDateBundleEntry, ultrasoundEDDBundleEntry);
  }

  if (ultrasoundGestationalAgeWeeks !== undefined && ultrasoundGestationalAgeDays !== undefined) {
    const ultrasoundEDDGestationalAgeBundleEntry = buildGACompositeFromUltrasoundBundleEntry(
      patientReferenceString,
      ultrasoundGestationalAgeWeeks,
      ultrasoundGestationalAgeDays,
      { request, resource },
    );
    entry.push(ultrasoundEDDGestationalAgeBundleEntry);
  }

  return [edd, entry];
}
